import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    Globe, Plus, Trash2, RefreshCw, CheckCircle, ArrowLeft,
    ChevronDown, ChevronUp, Flag, ListChecks, Users, ArrowRightCircle, Pencil, Settings, Newspaper, BarChart2, EyeOff, Eye, Download,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
    open: 'bg-blue-500 text-white',
    in_progress: 'bg-green-500 text-white',
    completed: 'bg-slate-500 text-white',
};

const ROUND_STATUS_COLORS = {
    upcoming: 'bg-slate-500 text-white',
    active: 'bg-green-500 text-white',
    complete: 'bg-blue-500 text-white',
};

function authHeaders() {
    const token = getToken();
    return { Authorization: `Bearer ${token}` };
}

function toApiDate(isoDate) {
    return isoDate ? isoDate.replace(/-/g, '') : '';
}

function formatDate(yyyymmdd) {
    if (!yyyymmdd || yyyymmdd.length < 8) return yyyymmdd;
    return `${yyyymmdd.slice(6, 8)}/${yyyymmdd.slice(4, 6)}/${yyyymmdd.slice(0, 4)}`;
}

export default function AdminFootballPage() {
    const { user } = useAuth();
    const [comps, setComps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [syncing, setSyncing] = useState(null);
    const [syncingFifa, setSyncingFifa] = useState(false);
    const [fifaSyncResult, setFifaSyncResult] = useState(null);
    const [mappingPlayers, setMappingPlayers] = useState(false);
    const [playerMapResult, setPlayerMapResult] = useState(null);
    const [remapping, setRemapping] = useState(false);
    const [remapResult, setRemapResult] = useState(null);
    const [remapProgress, setRemapProgress] = useState(null);
    const remapPollRef = useRef(null);
    const [syncingStats, setSyncingStats] = useState(false);
    const [statsSyncResult, setStatsSyncResult] = useState(null);
    const [statsSyncProgress, setStatsSyncProgress] = useState(null);
    const statsSyncPollRef = useRef(null);
    const [settling, setSettling] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [expandedComp, setExpandedComp] = useState(null);
    const [activeTab, setActiveTab] = useState({}); // compId → 'matches' | 'rounds'
    const [matches, setMatches] = useState({});
    const [rounds, setRounds] = useState({});

    // Round creation / edit dialog
    const [showCreateRound, setShowCreateRound] = useState(null); // compId or null
    const [editingRound, setEditingRound] = useState(null); // {compId, roundId} or null
    const [savingRound, setSavingRound] = useState(false);
    const [roundForm, setRoundForm] = useState({
        name: '',
        round_number: 1,
        status: 'upcoming',
        advancement_rule: 'all',
        advancement_count: '',
        carry_over_points: true,
        is_final: false,
        bonus_questions: [],
    });
    const [bonusCatalog, setBonusCatalog] = useState([]);

    // Match picker dialog
    const [matchPicker, setMatchPicker] = useState(null); // {compId, roundId, roundName} or null
    const [matchPool, setMatchPool] = useState({});
    const [pickerSelected, setPickerSelected] = useState(new Set());
    const [savingPicker, setSavingPicker] = useState(false);

    // Advance round
    const [advancing, setAdvancing] = useState(null); // roundId
    const [undoingAdvance, setUndoingAdvance] = useState(null); // roundId
    const [lmsBusy, setLmsBusy] = useState(null); // roundId currently advancing/voiding/undoing
    const [lmsPickStatus, setLmsPickStatus] = useState({}); // roundId -> {round, players}
    const [lmsPickStatusOpen, setLmsPickStatusOpen] = useState(null); // roundId currently expanded
    const [deleteRoundConfirm, setDeleteRoundConfirm] = useState(null); // {compId, roundId, roundName} or null
    const [deleteRoundInput, setDeleteRoundInput] = useState('');

    const [form, setForm] = useState({
        name: '',
        description: '',
        pa_competition_id: '',
        start_date: '',
        end_date: '',
        format: 'prediction',
    });

    // Edit competition dialog
    const [editingComp, setEditingComp] = useState(null); // comp object or null
    const [editCompForm, setEditCompForm] = useState({ name: '', description: '', pa_competition_id: '', start_date: '', end_date: '', status: '', hero_image: '', hero_image_x: 50, hero_image_y: 50, hero_image_opacity: 0.6 });
    const [savingComp, setSavingComp] = useState(false);

    // Football Hub settings
    const [hubCompId, setHubCompId] = useState('700');
    const [showBrackets, setShowBrackets] = useState(false);
    const [savingHub, setSavingHub] = useState(false);

    // Display / Kiosk page settings
    const [displayCompId, setDisplayCompId] = useState('700');
    const [displayTitle, setDisplayTitle] = useState('FIFA World Cup 2026');
    const [savingDisplay, setSavingDisplay] = useState(false);

    useEffect(() => {
        if (!user) return;              // wait for auth to resolve
        if (!user.is_admin) {
            toast.error('Admin access required');
            return;
        }
        loadComps();
        loadHubSettings();
        loadBonusCatalog();
    }, [user]);

    async function loadBonusCatalog() {
        try {
            const res = await axios.get(`${API_URL}/api/football/bonus-questions/catalog`, { headers: authHeaders() });
            setBonusCatalog(res.data.questions || []);
        } catch { /* non-critical */ }
    }

    async function loadHubSettings() {
        try {
            const res = await axios.get(`${API_URL}/api/admin/site-settings`, {
                headers: authHeaders(),
            });
            setHubCompId(String(res.data.hub_comp_id || 700));
            setShowBrackets(res.data.show_brackets === true);
            setDisplayCompId(String(res.data.display_comp_id || 700));
            setDisplayTitle(res.data.display_title || 'FIFA World Cup 2026');
        } catch {
            // keep default
        }
    }

    async function saveHubSettings() {
        setSavingHub(true);
        try {
            await axios.put(`${API_URL}/api/admin/site-settings`, {
                hub_comp_id: parseInt(hubCompId, 10) || 700,
                show_brackets: showBrackets,
            }, { headers: authHeaders() });
            toast.success('Football Hub settings updated');
        } catch {
            toast.error('Failed to save Hub settings');
        } finally {
            setSavingHub(false);
        }
    }

    async function saveDisplaySettings() {
        setSavingDisplay(true);
        try {
            await axios.put(`${API_URL}/api/admin/site-settings`, {
                display_comp_id: parseInt(displayCompId, 10) || 700,
                display_title: displayTitle,
            }, { headers: authHeaders() });
            toast.success('Display page settings updated');
        } catch {
            toast.error('Failed to save display settings');
        } finally {
            setSavingDisplay(false);
        }
    }

    async function loadComps() {
        try {
            const res = await axios.get(`${API_URL}/api/admin/football/competitions`, {
                headers: authHeaders(),
            });
            setComps(res.data.competitions || []);
        } catch {
            toast.error('Failed to load competitions');
        } finally {
            setLoading(false);
        }
    }

    const loadRounds = useCallback(async (compId) => {
        try {
            const res = await axios.get(
                `${API_URL}/api/admin/football/competitions/${compId}/rounds`,
                { headers: authHeaders() }
            );
            setRounds(prev => ({ ...prev, [compId]: res.data.rounds || [] }));
        } catch {
            toast.error('Failed to load rounds');
        }
    }, []);

    const loadMatchPool = useCallback(async (compId) => {
        if (matchPool[compId]) return;
        try {
            const res = await axios.get(
                `${API_URL}/api/admin/football/competitions/${compId}/matches/pool`,
                { headers: authHeaders() }
            );
            setMatchPool(prev => ({ ...prev, [compId]: res.data.matches || [] }));
        } catch {
            toast.error('Failed to load match pool');
        }
    }, [matchPool]);

    async function handleCreate() {
        if (!form.name || !form.pa_competition_id || !form.start_date || !form.end_date) {
            toast.error('Please fill in all required fields');
            return;
        }
        setCreating(true);
        try {
            await axios.post(`${API_URL}/api/admin/football/competitions`, {
                ...form,
                start_date: toApiDate(form.start_date),
                end_date: toApiDate(form.end_date),
            }, { headers: authHeaders() });
            toast.success('Competition created');
            setShowCreate(false);
            setForm({ name: '', description: '', pa_competition_id: '', start_date: '', end_date: '', format: 'prediction' });
            loadComps();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create competition');
        } finally {
            setCreating(false);
        }
    }

    async function handleBulkMapPlayers() {
        setMappingPlayers(true);
        setPlayerMapResult(null);
        try {
            const res = await axios.post(
                `${API_URL}/api/football/worldcup/bulk-map-all-players`,
                {},
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            setPlayerMapResult(res.data);
            toast.success(`Player mapping done — ${res.data.total_mapped} mapped across ${res.data.teams_processed} teams`);
        } catch (e) {
            toast.error('Player mapping failed: ' + (e.response?.data?.detail || e.message));
        } finally {
            setMappingPlayers(false);
        }
    }

    async function handleRemapAllPlayers() {
        setRemapping(true);
        setRemapResult(null);
        setRemapProgress(null);
        clearInterval(remapPollRef.current);
        try {
            const kickoff = await axios.post(
                `${API_URL}/api/admin/football/worldcup/remap-all-players`,
                {},
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            const { task_id } = kickoff.data;
            remapPollRef.current = setInterval(async () => {
                try {
                    const status = await axios.get(
                        `${API_URL}/api/admin/football/worldcup/remap-all-players/${task_id}`,
                        { headers: { Authorization: `Bearer ${getToken()}` } }
                    );
                    const d = status.data;
                    if (d.status === 'running') {
                        setRemapProgress({ done: d.teams_done || 0, total: d.total || 0, current: d.current_team || '' });
                    } else if (d.status === 'done') {
                        clearInterval(remapPollRef.current);
                        setRemapResult(d);
                        setRemapProgress(null);
                        setRemapping(false);
                        toast.success(`Re-map done — ${d.corrected} corrected, ${d.mapped} new across ${d.teams_processed} teams`);
                    }
                } catch {
                    clearInterval(remapPollRef.current);
                    setRemapping(false);
                    toast.error('Failed to get remap status');
                }
            }, 2500);
        } catch (e) {
            setRemapping(false);
            toast.error('Remap failed: ' + (e.response?.data?.detail || e.message));
        }
    }

    async function handleSyncTournamentStats(full = false) {
        setSyncingStats(true);
        setStatsSyncResult(null);
        setStatsSyncProgress(null);
        clearInterval(statsSyncPollRef.current);
        try {
            const url = `${API_URL}/api/admin/football/worldcup/sync-tournament-stats${full ? '?full=true' : ''}`;
            const kickoff = await axios.post(url, {}, { headers: { Authorization: `Bearer ${getToken()}` } });
            const { task_id } = kickoff.data;
            statsSyncPollRef.current = setInterval(async () => {
                try {
                    const status = await axios.get(
                        `${API_URL}/api/admin/football/worldcup/sync-tournament-stats/${task_id}`,
                        { headers: { Authorization: `Bearer ${getToken()}` } }
                    );
                    const d = status.data;
                    if (d.status === 'running') {
                        setStatsSyncProgress({ done: (d.fetched || 0) + (d.failed || 0), total: d.total || 0, mode: d.mode });
                    } else if (d.status === 'done' || d.status === 'error') {
                        clearInterval(statsSyncPollRef.current);
                        setStatsSyncResult(d);
                        setStatsSyncProgress(null);
                        setSyncingStats(false);
                        if (d.status === 'done') {
                            toast.success(`Stats synced — ${d.fetched} players updated (${d.mode})`);
                        } else {
                            toast.error('Stats sync error: ' + d.detail);
                        }
                    }
                } catch {
                    clearInterval(statsSyncPollRef.current);
                    setSyncingStats(false);
                    toast.error('Failed to get sync status');
                }
            }, 3000);
        } catch (e) {
            setSyncingStats(false);
            toast.error('Stats sync failed: ' + (e.response?.data?.detail || e.message));
        }
    }

    async function handleSyncFifaIntel() {
        setSyncingFifa(true);
        setFifaSyncResult(null);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/worldcup/sync-fifa-intel`,
                {},
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            setFifaSyncResult(res.data);
            toast.success(`FIFA intel synced — ${res.data.synced} teams matched`);
        } catch (e) {
            toast.error('FIFA sync failed: ' + (e.response?.data?.detail || e.message));
        } finally {
            setSyncingFifa(false);
        }
    }

    async function handleSync(compId) {
        setSyncing(compId);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/football/competitions/${compId}/sync`,
                {},
                { headers: authHeaders() }
            );
            toast.success(`Synced ${res.data.synced} matches`);
            loadComps();
            // Refresh match pool if loaded
            if (matchPool[compId]) {
                setMatchPool(prev => { const n = { ...prev }; delete n[compId]; return n; });
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Sync failed');
        } finally {
            setSyncing(null);
        }
    }

    async function handleSettle(compId) {
        setSettling(compId);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/football/competitions/${compId}/settle`,
                {},
                { headers: authHeaders() }
            );
            toast.success(`Settled ${res.data.settled} predictions`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Settle failed');
        } finally {
            setSettling(null);
        }
    }

    async function handleToggleHide(comp) {
        const newHidden = !comp.is_hidden;
        try {
            await axios.put(`${API_URL}/api/admin/football/competitions/${comp.id}`, { is_hidden: newHidden }, { headers: authHeaders() });
            setComps(prev => prev.map(c => c.id === comp.id ? { ...c, is_hidden: newHidden } : c));
            toast.success(newHidden ? 'Competition hidden from users' : 'Competition now visible');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update');
        }
    }

    async function handleDelete(compId) {
        if (!window.confirm('Delete this competition and all its data?')) return;
        setDeleting(compId);
        try {
            await axios.delete(`${API_URL}/api/admin/football/competitions/${compId}`, {
                headers: authHeaders(),
            });
            toast.success('Deleted');
            setComps(prev => prev.filter(c => c.id !== compId));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Delete failed');
        } finally {
            setDeleting(null);
        }
    }

    function openEditComp(comp) {
        // Dates stored as yyyyMMdd, date inputs need yyyy-MM-dd
        function toInputDate(d) {
            if (!d || d.length < 8) return '';
            return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
        }
        setEditCompForm({
            name: comp.name || '',
            description: comp.description || '',
            pa_competition_id: comp.pa_competition_id || '',
            start_date: toInputDate(comp.start_date),
            end_date: toInputDate(comp.end_date),
            status: comp.status || 'open',
            hero_image: comp.hero_image || '',
            hero_image_x: comp.hero_image_x ?? 50,
            hero_image_y: comp.hero_image_y ?? 50,
            hero_image_opacity: comp.hero_image_opacity ?? 0.6,
        });
        setEditingComp(comp);
    }

    async function handleSaveEditComp() {
        if (!editCompForm.name) { toast.error('Competition name required'); return; }
        setSavingComp(true);
        try {
            await axios.put(
                `${API_URL}/api/admin/football/competitions/${editingComp.id}`,
                {
                    name: editCompForm.name,
                    description: editCompForm.description,
                    pa_competition_id: editCompForm.pa_competition_id || undefined,
                    start_date: toApiDate(editCompForm.start_date),
                    end_date: toApiDate(editCompForm.end_date),
                    status: editCompForm.status,
                    hero_image: editCompForm.hero_image || undefined,
                    hero_image_x: editCompForm.hero_image ? editCompForm.hero_image_x : undefined,
                    hero_image_y: editCompForm.hero_image ? editCompForm.hero_image_y : undefined,
                    hero_image_opacity: editCompForm.hero_image ? editCompForm.hero_image_opacity : undefined,
                },
                { headers: authHeaders() }
            );
            toast.success('Competition updated');
            setEditingComp(null);
            loadComps();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update competition');
        } finally {
            setSavingComp(false);
        }
    }

    async function toggleExpand(compId) {
        if (expandedComp === compId) {
            setExpandedComp(null);
            return;
        }
        setExpandedComp(compId);
        const tab = activeTab[compId] || 'matches';
        setActiveTab(prev => ({ ...prev, [compId]: tab }));

        if (!matches[compId]) {
            try {
                const res = await axios.get(`${API_URL}/api/football/competitions/${compId}`, {
                    headers: authHeaders(),
                });
                setMatches(prev => ({ ...prev, [compId]: res.data.matches || [] }));
            } catch {
                toast.error('Failed to load matches');
            }
        }
        if (!rounds[compId]) {
            loadRounds(compId);
        }
    }

    // ── Round management ──────────────────────────────────────────────────────

    function openCreateRound(compId) {
        const existing = rounds[compId] || [];
        setRoundForm({
            name: '',
            round_number: existing.length + 1,
            advancement_rule: 'all',
            advancement_count: '',
            carry_over_points: true,
            is_final: false,
            bonus_questions: [],
        });
        setShowCreateRound(compId);
    }

    async function handleCreateRound() {
        const compId = showCreateRound;
        if (!roundForm.name) { toast.error('Round name required'); return; }
        setSavingRound(true);
        try {
            await axios.post(
                `${API_URL}/api/admin/football/competitions/${compId}/rounds`,
                {
                    name: roundForm.name,
                    round_number: parseInt(roundForm.round_number, 10) || 1,
                    advancement_rule: roundForm.advancement_rule,
                    advancement_count: roundForm.advancement_count ? parseInt(roundForm.advancement_count, 10) : null,
                    carry_over_points: roundForm.carry_over_points,
                    is_final: roundForm.is_final || false,
                    bonus_questions: roundForm.bonus_questions || [],
                },
                { headers: authHeaders() }
            );
            toast.success('Round created');
            setShowCreateRound(null);
            loadRounds(compId);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create round');
        } finally {
            setSavingRound(false);
        }
    }

    function openEditRound(compId, round) {
        setRoundForm({
            name: round.name,
            round_number: round.round_number,
            status: round.status || 'upcoming',
            advancement_rule: round.advancement_rule || 'all',
            advancement_count: round.advancement_count != null ? String(round.advancement_count) : '',
            carry_over_points: round.carry_over_points !== false,
            is_final: round.is_final || false,
            bonus_questions: round.bonus_questions || [],
        });
        setEditingRound({ compId, roundId: round.id });
    }

    async function handleSaveEditRound() {
        const { compId, roundId } = editingRound;
        if (!roundForm.name) { toast.error('Round name required'); return; }
        setSavingRound(true);
        try {
            await axios.patch(
                `${API_URL}/api/admin/football/competitions/${compId}/rounds/${roundId}`,
                {
                    name: roundForm.name,
                    round_number: parseInt(roundForm.round_number, 10) || 1,
                    status: roundForm.status,
                    advancement_rule: roundForm.advancement_rule,
                    advancement_count: roundForm.advancement_count ? parseInt(roundForm.advancement_count, 10) : null,
                    carry_over_points: roundForm.carry_over_points,
                    is_final: roundForm.is_final || false,
                    bonus_questions: roundForm.bonus_questions || [],
                },
                { headers: authHeaders() }
            );
            toast.success('Round updated');
            setEditingRound(null);
            loadRounds(compId);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update round');
        } finally {
            setSavingRound(false);
        }
    }

    function handleDeleteRound(compId, roundId) {
        const round = (rounds[compId] || []).find(r => r.id === roundId);
        setDeleteRoundInput('');
        setDeleteRoundConfirm({ compId, roundId, roundName: round?.name || 'this round' });
    }

    async function confirmDeleteRound() {
        if (!deleteRoundConfirm) return;
        const { compId, roundId } = deleteRoundConfirm;
        try {
            await axios.delete(
                `${API_URL}/api/admin/football/competitions/${compId}/rounds/${roundId}`,
                { headers: authHeaders() }
            );
            toast.success('Round deleted');
            loadRounds(compId);
            setMatchPool(prev => { const n = { ...prev }; delete n[compId]; return n; });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete round');
        } finally {
            setDeleteRoundConfirm(null);
            setDeleteRoundInput('');
        }
    }

    async function handleAdvanceRound(compId, roundId) {
        const roundName = (rounds[compId] || []).find(r => r.id === roundId)?.name || 'this round';
        if (!window.confirm(`Mark "${roundName}" as complete and process advancement?`)) return;
        setAdvancing(roundId);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/football/competitions/${compId}/rounds/${roundId}/advance`,
                {},
                { headers: authHeaders() }
            );
            toast.success(`Round complete — ${res.data.advancing} advancing, ${res.data.eliminated} eliminated`);
            loadRounds(compId);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to advance round');
        } finally {
            setAdvancing(null);
        }
    }

    async function handleUndoAdvance(compId, roundId) {
        const roundName = (rounds[compId] || []).find(r => r.id === roundId)?.name || 'this round';
        if (!window.confirm(`Undo advance for "${roundName}"? This will un-eliminate players and reset the round to active.`)) return;
        setUndoingAdvance(roundId);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/football/competitions/${compId}/rounds/${roundId}/undo-advance`,
                {},
                { headers: authHeaders() }
            );
            toast.success(`Undone — ${res.data.un_eliminated} players un-eliminated`);
            loadRounds(compId);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to undo advance');
        } finally {
            setUndoingAdvance(null);
        }
    }

    // ── Last Man Standing: gameweek advance / void / undo ────────────────────────

    async function handleAdvanceLmsRound(compId, roundId) {
        const roundName = (rounds[compId] || []).find(r => r.id === roundId)?.name || 'this gameweek';
        if (!window.confirm(`Advance "${roundName}"? Every active player's pick will be resolved — a draw or loss eliminates them.`)) return;
        setLmsBusy(roundId);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/football/lms/competitions/${compId}/rounds/${roundId}/advance`,
                {}, { headers: authHeaders() }
            );
            if (!res.data.round_complete) {
                const pending = res.data.pending_matches || [];
                const names = pending
                    .map(m => `${m.home_team_name || '?'} v ${m.away_team_name || '?'}`)
                    .slice(0, 4)
                    .join(', ');
                toast.warning(
                    `${res.data.unresolved} pick(s) have no full-time result yet — those players stay in and the gameweek is still open.` +
                    (names ? ` Waiting on: ${names}${pending.length > 4 ? '…' : ''}` : '')
                );
            } else if (res.data.outcome === 'winner') {
                toast.success(`We have a champion! 🏆 (${res.data.survived} survived, ${res.data.eliminated} eliminated)`);
            } else if (res.data.outcome === 'no_winner') {
                toast.warning('Everyone was eliminated this gameweek — no winner. Set up a fresh competition to restart.');
            } else {
                toast.success(`Gameweek complete — ${res.data.survived} survive, ${res.data.eliminated} eliminated`);
            }
            loadRounds(compId);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to advance gameweek');
        } finally {
            setLmsBusy(null);
        }
    }

    async function handleResettleLmsRound(compId, roundId) {
        const roundName = (rounds[compId] || []).find(r => r.id === roundId)?.name || 'this gameweek';
        if (!window.confirm(`Recheck "${roundName}" against the finished fixtures? Anyone knocked out by a result that has since changed — or that was never final — comes back in.`)) return;
        setLmsBusy(roundId);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/football/lms/competitions/${compId}/rounds/${roundId}/resettle`,
                {}, { headers: authHeaders() }
            );
            const bits = [`${res.data.reinstated} reinstated`, `${res.data.newly_eliminated} eliminated`];
            if (res.data.unresolved) bits.push(`${res.data.unresolved} still unresolved`);
            toast.success(`Rechecked — ${bits.join(', ')}. ${res.data.still_in} still in.`);
            loadRounds(compId);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to recheck gameweek');
        } finally {
            setLmsBusy(null);
        }
    }

    async function handleVoidLmsRound(compId, roundId) {
        const roundName = (rounds[compId] || []).find(r => r.id === roundId)?.name || 'this gameweek';
        const reason = window.prompt(`Void "${roundName}"? Nobody will be eliminated and every pick this gameweek is reinstated.\n\nOptional reason (e.g. "8 postponed, weather"):`);
        if (reason === null) return; // cancelled
        setLmsBusy(roundId);
        try {
            await axios.post(
                `${API_URL}/api/admin/football/lms/competitions/${compId}/rounds/${roundId}/void`,
                { reason }, { headers: authHeaders() }
            );
            toast.success(`Gameweek voided — everyone advances, picked teams reinstated`);
            loadRounds(compId);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to void gameweek');
        } finally {
            setLmsBusy(null);
        }
    }

    async function handleUndoLmsRound(compId, roundId) {
        const roundName = (rounds[compId] || []).find(r => r.id === roundId)?.name || 'this gameweek';
        if (!window.confirm(`Undo "${roundName}"? This reverses an advance or a void — eliminations and voided status are both reverted.`)) return;
        setLmsBusy(roundId);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/football/lms/competitions/${compId}/rounds/${roundId}/undo-advance`,
                {}, { headers: authHeaders() }
            );
            toast.success(`Undone — ${res.data.un_eliminated} player(s) un-eliminated`);
            loadRounds(compId);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to undo gameweek');
        } finally {
            setLmsBusy(null);
        }
    }

    async function toggleLmsPickStatus(compId, roundId) {
        if (lmsPickStatusOpen === roundId) {
            setLmsPickStatusOpen(null);
            return;
        }
        setLmsPickStatusOpen(roundId);
        if (lmsPickStatus[roundId]) return; // already fetched
        try {
            const res = await axios.get(
                `${API_URL}/api/admin/football/lms/competitions/${compId}/pick-status`,
                { params: { round_id: roundId }, headers: authHeaders() }
            );
            setLmsPickStatus(prev => ({ ...prev, [roundId]: res.data }));
        } catch {
            toast.error('Failed to load pick status');
        }
    }

    // ── Match picker ──────────────────────────────────────────────────────────

    async function openMatchPicker(compId, roundId, roundName) {
        await loadMatchPool(compId);
        const pool = matchPool[compId] || [];
        const preSelected = new Set(
            pool.filter(m => m.round_id === roundId).map(m => m.pa_match_id)
        );
        setPickerSelected(preSelected);
        setMatchPicker({ compId, roundId, roundName });
    }

    // Re-read pool from state after loadMatchPool, so use a separate effect-triggered open
    async function openMatchPickerFresh(compId, roundId, roundName) {
        try {
            const res = await axios.get(
                `${API_URL}/api/admin/football/competitions/${compId}/matches/pool`,
                { headers: authHeaders() }
            );
            const pool = res.data.matches || [];
            setMatchPool(prev => ({ ...prev, [compId]: pool }));
            const preSelected = new Set(
                pool.filter(m => m.round_id === roundId).map(m => m.pa_match_id)
            );
            setPickerSelected(preSelected);
            setMatchPicker({ compId, roundId, roundName });
        } catch {
            toast.error('Failed to load matches');
        }
    }

    async function handleSavePicker() {
        if (!matchPicker) return;
        const { compId, roundId } = matchPicker;
        setSavingPicker(true);
        try {
            const res = await axios.put(
                `${API_URL}/api/admin/football/competitions/${compId}/rounds/${roundId}/matches`,
                { match_ids: [...pickerSelected] },
                { headers: authHeaders() }
            );
            toast.success(`${res.data.assigned} matches assigned`);
            setMatchPicker(null);
            // Refresh pool
            setMatchPool(prev => { const n = { ...prev }; delete n[compId]; return n; });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save assignments');
        } finally {
            setSavingPicker(false);
        }
    }

    // Group matches by date for picker display
    function groupMatchesByDate(matchList) {
        const groups = {};
        (matchList || []).forEach(m => {
            const d = m.match_date || '?';
            if (!groups[d]) groups[d] = [];
            groups[d].push(m);
        });
        return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
    }

    return (
        <div className="container-app py-6 space-y-6">
            <div className="flex items-center gap-3">
                <Link to="/admin/dashboard">
                    <Button variant="ghost" size="icon">
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                </Link>
                <div className="flex-1">
                    <h1 className="text-xl font-bold font-heading flex items-center gap-2 page-title">
                        <Globe className="h-5 w-5 text-blue-500" />
                        Football Competitions
                    </h1>
                    <p className="text-sm text-muted-foreground">Manage score prediction competitions</p>
                </div>
                <Link to="/admin/football/engagement">
                    <Button variant="outline" className="gap-2">
                        <BarChart2 className="h-4 w-4" />
                        Engagement
                    </Button>
                </Link>
                <Button onClick={() => setShowCreate(true)} className="gap-2">
                    <Plus className="h-4 w-4" />
                    New Competition
                </Button>
            </div>

            {/* Football Hub Settings */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Newspaper className="h-5 w-5 text-primary" />
                        Football Hub Settings
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div>
                        <Label className="text-sm font-medium mb-2 block">PA Competition ID</Label>
                        <p className="text-xs text-muted-foreground mb-3">
                            Controls which competition powers the Football Hub — fixtures, table, and top scorers.
                        </p>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {[
                                { label: 'World Cup 2026', id: '700' },
                                { label: 'Premier League', id: '100' },
                                { label: 'Championship', id: '101' },
                                { label: 'FA Cup', id: '500' },
                            ].map(preset => (
                                <Button
                                    key={preset.id}
                                    variant={hubCompId === preset.id ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setHubCompId(preset.id)}
                                >
                                    {preset.label}
                                </Button>
                            ))}
                        </div>
                        <div className="flex items-center gap-3 max-w-xs">
                            <Input
                                value={hubCompId}
                                onChange={e => setHubCompId(e.target.value)}
                                placeholder="e.g. 700"
                                className="w-32"
                            />
                            <Button onClick={saveHubSettings} disabled={savingHub} className="gap-2">
                                {savingHub ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                                Save
                            </Button>
                        </div>
                        <label className="flex items-center gap-2 mt-4 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showBrackets}
                                onChange={e => setShowBrackets(e.target.checked)}
                                className="h-4 w-4"
                            />
                            <span className="text-sm">Show knockout bracket (replaces the league table on the Hub)</span>
                        </label>
                        <p className="text-xs text-muted-foreground mt-1">Remember to click Save after toggling.</p>
                    </div>
                </CardContent>
            </Card>

            {/* Display / Kiosk Page Settings */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <BarChart2 className="h-5 w-5 text-amber-500" />
                        Display / Kiosk Page Settings
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <p className="text-xs text-muted-foreground">
                        Controls which competition powers the kiosk display page — fixtures, groups, top scorers and the live stats strip.
                    </p>

                    <div>
                        <Label className="text-sm font-medium mb-2 block">PA Competition ID</Label>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {[
                                { label: 'World Cup 2026', id: '700' },
                                { label: 'Premier League', id: '100' },
                                { label: 'Championship', id: '101' },
                                { label: 'FA Cup', id: '500' },
                            ].map(preset => (
                                <Button
                                    key={preset.id}
                                    variant={displayCompId === preset.id ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setDisplayCompId(preset.id)}
                                >
                                    {preset.label}
                                </Button>
                            ))}
                        </div>
                        <Input
                            value={displayCompId}
                            onChange={e => setDisplayCompId(e.target.value)}
                            placeholder="e.g. 700"
                            className="w-32"
                        />
                    </div>

                    <div>
                        <Label className="text-sm font-medium mb-2 block">Display Title</Label>
                        <p className="text-xs text-muted-foreground mb-2">Shown in the kiosk header instead of "FIFA World Cup 2026".</p>
                        <Input
                            value={displayTitle}
                            onChange={e => setDisplayTitle(e.target.value)}
                            placeholder="e.g. FIFA World Cup 2026"
                            className="max-w-xs"
                        />
                    </div>

                    <Button onClick={saveDisplaySettings} disabled={savingDisplay} className="gap-2">
                        {savingDisplay ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                        Save Display Settings
                    </Button>
                </CardContent>
            </Card>

            {/* FIFA World Cup Team Intel Sync */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Globe className="h-5 w-5 text-emerald-500" />
                        World Cup Team Intel
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                        Fetches a one-time snapshot of FIFA world rankings and recent match results for all 48 World Cup teams.
                        Run this once before the tournament — no need to update after that. Users can hover over any team badge
                        in the WC predictor to see pre-tournament data.
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                        <Button
                            onClick={handleSyncFifaIntel}
                            disabled={syncingFifa}
                            variant={fifaSyncResult ? 'outline' : 'default'}
                            className="gap-2"
                        >
                            {syncingFifa
                                ? <RefreshCw className="h-4 w-4 animate-spin" />
                                : <Download className="h-4 w-4" />}
                            {syncingFifa ? 'Syncing FIFA Data...' : fifaSyncResult ? 'Re-sync FIFA Data' : 'Sync FIFA Team Intel'}
                        </Button>
                        <Link to="/admin/worldcup/mappings">
                            <Button variant="outline" className="gap-2">
                                <Globe className="h-4 w-4" />
                                View / Fix Mappings
                            </Button>
                        </Link>
                        <Link to="/admin/statsapi/mappings">
                            <Button variant="outline" className="gap-2">
                                <ArrowRightCircle className="h-4 w-4" />
                                Shot Map Team Mappings
                            </Button>
                        </Link>
                        <Button
                            onClick={handleBulkMapPlayers}
                            disabled={mappingPlayers}
                            variant="outline"
                            className="gap-2"
                        >
                            {mappingPlayers
                                ? <RefreshCw className="h-4 w-4 animate-spin" />
                                : <Users className="h-4 w-4" />}
                            {mappingPlayers ? 'Mapping Players...' : 'Map All WC Players'}
                        </Button>
                        <Button
                            onClick={handleRemapAllPlayers}
                            disabled={remapping}
                            variant="outline"
                            className="gap-2"
                        >
                            {remapping
                                ? <RefreshCw className="h-4 w-4 animate-spin" />
                                : <Users className="h-4 w-4" />}
                            {remapping
                                ? remapProgress?.total
                                    ? `Remapping… ${remapProgress.done}/${remapProgress.total}${remapProgress.current ? ` (${remapProgress.current})` : ''}`
                                    : 'Starting…'
                                : 'Re-map All Players'}
                        </Button>
                        <Button
                            onClick={() => handleSyncTournamentStats(false)}
                            disabled={syncingStats}
                            variant="outline"
                            className="gap-2"
                        >
                            {syncingStats
                                ? <RefreshCw className="h-4 w-4 animate-spin" />
                                : <Download className="h-4 w-4" />}
                            {syncingStats
                                ? statsSyncProgress?.total
                                    ? `Syncing… ${statsSyncProgress.done}/${statsSyncProgress.total}`
                                    : 'Starting…'
                                : 'Sync WC Stats (Smart)'}
                        </Button>
                        <Button
                            onClick={() => handleSyncTournamentStats(true)}
                            disabled={syncingStats}
                            variant="outline"
                            className="gap-2 text-muted-foreground"
                        >
                            {syncingStats ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                            Full Sync (All Players)
                        </Button>
                        <Link to="/admin/worldcup/player-mappings">
                            <Button variant="outline" className="gap-2">
                                <Users className="h-4 w-4" />
                                View Unmapped Players
                            </Button>
                        </Link>
                        <Link to="/admin/worldcup/mapping-review">
                            <Button variant="outline" className="gap-2">
                                <Users className="h-4 w-4" />
                                Review Mappings
                            </Button>
                        </Link>
                        {remapResult && (
                            <span className="text-sm text-emerald-500 font-medium">
                                ✓ {remapResult.corrected} corrected, {remapResult.mapped} new mappings, {remapResult.no_match} unmatched across {remapResult.teams_processed} teams
                            </span>
                        )}
                        {statsSyncResult?.status === 'done' && (
                            <span className="text-sm text-emerald-500 font-medium">
                                ✓ {statsSyncResult.fetched} players updated, {statsSyncResult.failed} failed
                                {statsSyncResult.mode && <span className="text-muted-foreground font-normal ml-1">({statsSyncResult.mode})</span>}
                            </span>
                        )}
                        {fifaSyncResult && (
                            <span className="text-sm text-emerald-500 font-medium">
                                ✓ {fifaSyncResult.synced} teams synced from {fifaSyncResult.pa_teams_in_db} in DB
                                {fifaSyncResult.unmatched_sample?.length > 0 && (
                                    <span className="text-muted-foreground ml-2 text-xs">
                                        (unmatched: {fifaSyncResult.unmatched_sample.join(', ')})
                                    </span>
                                )}
                            </span>
                        )}
                        {playerMapResult && (
                            <span className="text-sm text-emerald-500 font-medium">
                                ✓ {playerMapResult.total_mapped} new mappings, {playerMapResult.total_skipped} skipped, {playerMapResult.total_unmatched} unmatched across {playerMapResult.teams_processed} teams
                            </span>
                        )}
                    </div>
                </CardContent>
            </Card>

            {loading ? (
                <div className="space-y-3">
                    {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32 w-full" />)}
                </div>
            ) : comps.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                        <Globe className="h-8 w-8 mx-auto mb-3 opacity-30" />
                        <p>No competitions yet. Create one to get started.</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {comps.map(comp => (
                        <Card key={comp.id}>
                            <CardHeader className="pb-3">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <CardTitle className="text-base">{comp.name}</CardTitle>
                                        {comp.description && (
                                            <p className="text-sm text-muted-foreground mt-0.5">{comp.description}</p>
                                        )}
                                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                                            <Badge className={STATUS_COLORS[comp.status] || 'bg-slate-500 text-white'}>
                                                {comp.status === 'ended_no_winner' ? 'no winner' : comp.status}
                                            </Badge>
                                            {comp.format === 'last_man_standing' && (
                                                <Badge variant="outline" className="gap-1 text-primary border-primary/40">
                                                    Last Man Standing
                                                </Badge>
                                            )}
                                            {comp.is_hidden && (
                                                <Badge variant="outline" className="gap-1 text-muted-foreground border-muted-foreground/40">
                                                    <EyeOff className="h-3 w-3" /> Hidden
                                                </Badge>
                                            )}
                                            <span className="text-xs text-muted-foreground">
                                                PA ID: {comp.pa_competition_id}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                                {formatDate(comp.start_date)} → {formatDate(comp.end_date)}
                                            </span>
                                            {comp.match_count != null && (
                                                <span className="text-xs text-muted-foreground">
                                                    {comp.match_count} matches synced
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardFooter className="flex flex-wrap gap-2 pt-0">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-1.5"
                                    onClick={() => handleSync(comp.id)}
                                    disabled={syncing === comp.id}
                                >
                                    <RefreshCw className={`h-3.5 w-3.5 ${syncing === comp.id ? 'animate-spin' : ''}`} />
                                    Sync Fixtures
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-1.5"
                                    onClick={() => handleSettle(comp.id)}
                                    disabled={settling === comp.id}
                                >
                                    <CheckCircle className="h-3.5 w-3.5" />
                                    Settle Points
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-1.5"
                                    onClick={() => openEditComp(comp)}
                                >
                                    <Settings className="h-3.5 w-3.5" />
                                    Edit
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-1.5"
                                    onClick={() => toggleExpand(comp.id)}
                                >
                                    {expandedComp === comp.id
                                        ? <ChevronUp className="h-3.5 w-3.5" />
                                        : <ChevronDown className="h-3.5 w-3.5" />}
                                    {expandedComp === comp.id ? 'Hide' : 'Manage'}
                                </Button>
                                <Link to={`/admin/football/engagement/${comp.id}`}>
                                    <Button size="sm" variant="outline" className="gap-1.5">
                                        <BarChart2 className="h-3.5 w-3.5" />
                                        Engagement
                                    </Button>
                                </Link>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-1.5"
                                    onClick={() => handleToggleHide(comp)}
                                >
                                    {comp.is_hidden ? <><Eye className="h-3.5 w-3.5" />Show</> : <><EyeOff className="h-3.5 w-3.5" />Hide</>}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="destructive"
                                    className="gap-1.5 ml-auto"
                                    onClick={() => handleDelete(comp.id)}
                                    disabled={deleting === comp.id}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                    Delete
                                </Button>
                            </CardFooter>

                            {expandedComp === comp.id && (
                                <CardContent className="pt-0 border-t">
                                    {/* Tabs */}
                                    <div className="flex gap-2 mt-3 mb-3">
                                        <button
                                            onClick={() => setActiveTab(prev => ({ ...prev, [comp.id]: 'matches' }))}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                                (activeTab[comp.id] || 'matches') === 'matches'
                                                    ? 'bg-primary text-primary-foreground'
                                                    : 'bg-muted text-muted-foreground hover:text-foreground'
                                            }`}
                                        >
                                            <ListChecks className="h-3.5 w-3.5" />
                                            Matches
                                        </button>
                                        <button
                                            onClick={() => setActiveTab(prev => ({ ...prev, [comp.id]: 'rounds' }))}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                                (activeTab[comp.id] || 'matches') === 'rounds'
                                                    ? 'bg-primary text-primary-foreground'
                                                    : 'bg-muted text-muted-foreground hover:text-foreground'
                                            }`}
                                        >
                                            <Flag className="h-3.5 w-3.5" />
                                            Rounds
                                            {rounds[comp.id]?.length > 0 && (
                                                <span className="ml-1 bg-white/20 text-xs px-1.5 py-0.5 rounded-full">
                                                    {rounds[comp.id].length}
                                                </span>
                                            )}
                                        </button>
                                    </div>

                                    {/* Matches tab */}
                                    {(activeTab[comp.id] || 'matches') === 'matches' && (
                                        !matches[comp.id] ? (
                                            <Skeleton className="h-20 w-full" />
                                        ) : matches[comp.id].length === 0 ? (
                                            <p className="text-sm text-muted-foreground py-3 text-center">
                                                No matches synced yet — click "Sync Fixtures" first.
                                            </p>
                                        ) : (
                                            <div className="space-y-1.5 max-h-72 overflow-y-auto">
                                                {matches[comp.id].map(m => (
                                                    <div
                                                        key={m.pa_match_id}
                                                        className="flex items-center justify-between text-xs py-1.5 px-2 rounded bg-muted/50"
                                                    >
                                                        <span className="text-muted-foreground w-20 flex-shrink-0">
                                                            {m.match_date}
                                                        </span>
                                                        <span className="flex-1 text-center font-medium">
                                                            {m.home_team_name} v {m.away_team_name}
                                                        </span>
                                                        <span className="w-24 text-right text-muted-foreground flex-shrink-0">
                                                            {m.status === 'finished'
                                                                ? `${m.home_score ?? '?'} – ${m.away_score ?? '?'}`
                                                                : m.status}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )
                                    )}

                                    {/* Rounds tab */}
                                    {(activeTab[comp.id] || 'matches') === 'rounds' && (
                                        <div className="space-y-3">
                                            {!rounds[comp.id] ? (
                                                <Skeleton className="h-20 w-full" />
                                            ) : rounds[comp.id].length === 0 ? (
                                                <p className="text-sm text-muted-foreground py-3 text-center">
                                                    No rounds yet — add one to create a tournament structure.
                                                </p>
                                            ) : (
                                                rounds[comp.id].map(round => (
                                                    <div
                                                        key={round.id}
                                                        className="flex flex-wrap items-center gap-2 py-2.5 px-3 rounded-md bg-muted/40 border"
                                                    >
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="text-sm font-semibold">
                                                                    Round {round.round_number}: {round.name}
                                                                </span>
                                                                <Badge className={ROUND_STATUS_COLORS[round.status] || ''}>
                                                                    {round.status}
                                                                </Badge>
                                                                {round.carry_over_points ? (
                                                                    <span className="text-xs text-muted-foreground">carries pts</span>
                                                                ) : (
                                                                    <span className="text-xs text-amber-500">resets pts</span>
                                                                )}
                                                                {round.advancement_rule === 'top_n' && round.advancement_count && (
                                                                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                                                        <Users className="h-3 w-3" />
                                                                        Top {round.advancement_count} advance
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-7 text-xs gap-1"
                                                                onClick={() => openMatchPickerFresh(comp.id, round.id, round.name)}
                                                            >
                                                                <ListChecks className="h-3 w-3" />
                                                                Pick Matches
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-7 text-xs gap-1"
                                                                onClick={() => openEditRound(comp.id, round)}
                                                            >
                                                                <Pencil className="h-3 w-3" />
                                                                Edit
                                                            </Button>
                                                            {comp.format === 'last_man_standing' ? (
                                                                round.status !== 'complete' ? (
                                                                    <>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            className="h-7 text-xs gap-1"
                                                                            onClick={() => toggleLmsPickStatus(comp.id, round.id)}
                                                                        >
                                                                            <Users className="h-3 w-3" />
                                                                            Who's picked
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            className="h-7 text-xs gap-1"
                                                                            onClick={() => handleAdvanceLmsRound(comp.id, round.id)}
                                                                            disabled={lmsBusy === round.id}
                                                                        >
                                                                            <ArrowRightCircle className="h-3 w-3" />
                                                                            Advance Gameweek
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            className="h-7 text-xs gap-1"
                                                                            onClick={() => handleResettleLmsRound(comp.id, round.id)}
                                                                            disabled={lmsBusy === round.id}
                                                                            title="Recompute every pick from the fixtures as they stand now"
                                                                        >
                                                                            <RefreshCw className="h-3 w-3" />
                                                                            Recheck Results
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            className="h-7 text-xs gap-1 text-amber-600 border-amber-300 hover:bg-amber-50"
                                                                            onClick={() => handleVoidLmsRound(comp.id, round.id)}
                                                                            disabled={lmsBusy === round.id}
                                                                        >
                                                                            Void Gameweek
                                                                        </Button>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        className="h-7 text-xs gap-1"
                                                                        onClick={() => handleResettleLmsRound(comp.id, round.id)}
                                                                        disabled={lmsBusy === round.id}
                                                                        title="Recompute every pick from the fixtures as they stand now"
                                                                    >
                                                                        <RefreshCw className="h-3 w-3" />
                                                                        Recheck Results
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        className="h-7 text-xs gap-1 text-amber-600 border-amber-300 hover:bg-amber-50"
                                                                        onClick={() => handleUndoLmsRound(comp.id, round.id)}
                                                                        disabled={lmsBusy === round.id}
                                                                    >
                                                                        <ArrowRightCircle className="h-3 w-3" />
                                                                        Undo{round.voided ? ' Void' : ' Advance'}
                                                                    </Button>
                                                                    </>
                                                                )
                                                            ) : round.status !== 'complete' ? (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="h-7 text-xs gap-1"
                                                                    onClick={() => handleAdvanceRound(comp.id, round.id)}
                                                                    disabled={advancing === round.id}
                                                                >
                                                                    <ArrowRightCircle className="h-3 w-3" />
                                                                    Advance
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="h-7 text-xs gap-1 text-amber-600 border-amber-300 hover:bg-amber-50"
                                                                    onClick={() => handleUndoAdvance(comp.id, round.id)}
                                                                    disabled={undoingAdvance === round.id}
                                                                >
                                                                    <ArrowRightCircle className="h-3 w-3" />
                                                                    Undo Advance
                                                                </Button>
                                                            )}
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                                                onClick={() => handleDeleteRound(comp.id, round.id)}
                                                            >
                                                                <Trash2 className="h-3 w-3" />
                                                            </Button>
                                                        </div>
                                                        {comp.format === 'last_man_standing' && lmsPickStatusOpen === round.id && (
                                                            <div className="w-full mt-2 pt-2 border-t border-border/50">
                                                                {!lmsPickStatus[round.id] ? (
                                                                    <Skeleton className="h-10 w-full" />
                                                                ) : lmsPickStatus[round.id].players.length === 0 ? (
                                                                    <p className="text-xs text-muted-foreground py-1">No active players.</p>
                                                                ) : (
                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {lmsPickStatus[round.id].players.map(p => (
                                                                            <span
                                                                                key={p.user_id}
                                                                                className={`text-[11px] px-2 py-1 rounded-md border ${
                                                                                    p.has_picked
                                                                                        ? 'bg-green-500/10 border-green-500/30 text-green-600'
                                                                                        : 'bg-amber-500/10 border-amber-500/30 text-amber-600'
                                                                                }`}
                                                                            >
                                                                                {p.username}{p.has_picked ? ` · ${p.team_name}` : ' · no pick yet'}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="gap-1.5 w-full"
                                                onClick={() => openCreateRound(comp.id)}
                                            >
                                                <Plus className="h-3.5 w-3.5" />
                                                Add Round
                                            </Button>
                                        </div>
                                    )}
                                </CardContent>
                            )}
                        </Card>
                    ))}
                </div>
            )}

            {/* Create competition dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create Football Competition</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label>Competition Name *</Label>
                            <Input
                                placeholder="e.g. Premier League Prediction League"
                                value={form.name}
                                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Description</Label>
                            <Textarea
                                placeholder="Optional description"
                                value={form.description}
                                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                                rows={2}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Format *</Label>
                            <Select
                                value={form.format}
                                onValueChange={v => setForm(p => ({ ...p, format: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select format" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="prediction">Score predictions (rounds, bonus questions, points)</SelectItem>
                                    <SelectItem value="last_man_standing">Last Man Standing (pick one team to survive each gameweek)</SelectItem>
                                </SelectContent>
                            </Select>
                            {form.format === 'last_man_standing' && (
                                <p className="text-xs text-muted-foreground">
                                    Joining locks once gameweek 1 kicks off. There's no scoring — a draw or loss eliminates,
                                    last player standing wins.
                                </p>
                            )}
                        </div>
                        <div className="space-y-1.5">
                            <Label>PA Competition *</Label>
                            <Select
                                value={form.pa_competition_id}
                                onValueChange={v => setForm(p => ({ ...p, pa_competition_id: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select competition" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="100">Premier League (100)</SelectItem>
                                    <SelectItem value="700">FIFA World Cup 2026 (700)</SelectItem>
                                    <SelectItem value="101">Championship (101)</SelectItem>
                                    <SelectItem value="200">Champions League (200)</SelectItem>
                                    <SelectItem value="other">Other — type below</SelectItem>
                                </SelectContent>
                            </Select>
                            {form.pa_competition_id === 'other' && (
                                <Input
                                    placeholder="Enter PA competition ID"
                                    onChange={e => setForm(p => ({ ...p, pa_competition_id: e.target.value }))}
                                    className="mt-1.5"
                                />
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Start Date *</Label>
                                <input
                                    type="date"
                                    value={form.start_date}
                                    onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                                    className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>End Date *</Label>
                                <input
                                    type="date"
                                    value={form.end_date}
                                    onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                                    className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                        <Button onClick={handleCreate} disabled={creating}>
                            {creating ? 'Creating…' : 'Create Competition'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit competition dialog */}
            <Dialog open={!!editingComp} onOpenChange={v => !v && setEditingComp(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Competition</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label>Competition Name *</Label>
                            <Input
                                value={editCompForm.name}
                                onChange={e => setEditCompForm(p => ({ ...p, name: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Description</Label>
                            <Textarea
                                value={editCompForm.description}
                                onChange={e => setEditCompForm(p => ({ ...p, description: e.target.value }))}
                                rows={2}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Hero Image URL <span className="text-muted-foreground font-normal">(optional — right side of featured card)</span></Label>
                            <Input
                                value={editCompForm.hero_image}
                                onChange={e => setEditCompForm(p => ({ ...p, hero_image: e.target.value }))}
                                placeholder="https://..."
                            />
                            {editCompForm.hero_image && (
                                <div className="space-y-3 pt-1">
                                    {/* Live preview */}
                                    <div className="relative h-24 rounded-lg overflow-hidden bg-slate-800 border border-border/40">
                                        <img
                                            src={editCompForm.hero_image}
                                            alt="Preview"
                                            className="absolute inset-0 w-full h-full object-cover"
                                            style={{
                                                objectPosition: `${editCompForm.hero_image_x}% ${editCompForm.hero_image_y}%`,
                                                opacity: editCompForm.hero_image_opacity,
                                            }}
                                            onError={e => e.target.style.display='none'}
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                            <span className="text-[10px] text-white/40 uppercase tracking-widest">Preview</span>
                                        </div>
                                    </div>
                                    {/* X position */}
                                    <div className="space-y-1">
                                        <div className="flex justify-between text-xs text-muted-foreground">
                                            <span>Position X (left → right)</span>
                                            <span className="font-mono">{editCompForm.hero_image_x}%</span>
                                        </div>
                                        <input type="range" min="0" max="100"
                                            value={editCompForm.hero_image_x}
                                            onChange={e => setEditCompForm(p => ({ ...p, hero_image_x: Number(e.target.value) }))}
                                            className="w-full accent-amber-400"
                                        />
                                    </div>
                                    {/* Y position */}
                                    <div className="space-y-1">
                                        <div className="flex justify-between text-xs text-muted-foreground">
                                            <span>Position Y (top → bottom)</span>
                                            <span className="font-mono">{editCompForm.hero_image_y}%</span>
                                        </div>
                                        <input type="range" min="0" max="100"
                                            value={editCompForm.hero_image_y}
                                            onChange={e => setEditCompForm(p => ({ ...p, hero_image_y: Number(e.target.value) }))}
                                            className="w-full accent-amber-400"
                                        />
                                    </div>
                                    {/* Opacity */}
                                    <div className="space-y-1">
                                        <div className="flex justify-between text-xs text-muted-foreground">
                                            <span>Opacity</span>
                                            <span className="font-mono">{Math.round(editCompForm.hero_image_opacity * 100)}%</span>
                                        </div>
                                        <input type="range" min="10" max="100"
                                            value={Math.round(editCompForm.hero_image_opacity * 100)}
                                            onChange={e => setEditCompForm(p => ({ ...p, hero_image_opacity: Number(e.target.value) / 100 }))}
                                            className="w-full accent-amber-400"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="space-y-1.5">
                            <Label>PA Competition ID</Label>
                            <Input
                                value={editCompForm.pa_competition_id}
                                onChange={e => setEditCompForm(p => ({ ...p, pa_competition_id: e.target.value }))}
                                placeholder="e.g. 100"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Start Date</Label>
                                <input
                                    type="date"
                                    value={editCompForm.start_date}
                                    onChange={e => setEditCompForm(p => ({ ...p, start_date: e.target.value }))}
                                    className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>End Date</Label>
                                <input
                                    type="date"
                                    value={editCompForm.end_date}
                                    onChange={e => setEditCompForm(p => ({ ...p, end_date: e.target.value }))}
                                    className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Status</Label>
                            <Select
                                value={editCompForm.status}
                                onValueChange={v => setEditCompForm(p => ({ ...p, status: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="open">Open</SelectItem>
                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditingComp(null)}>Cancel</Button>
                        <Button onClick={handleSaveEditComp} disabled={savingComp}>
                            {savingComp ? 'Saving…' : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create round dialog */}
            <Dialog open={!!showCreateRound} onOpenChange={v => !v && setShowCreateRound(null)}>
                <DialogContent className="flex flex-col max-h-[90vh]">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>Add Round</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2 overflow-y-auto flex-1 pr-1">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Round Name *</Label>
                                <Input
                                    placeholder="e.g. Group Stage Week 1"
                                    value={roundForm.name}
                                    onChange={e => setRoundForm(p => ({ ...p, name: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Round Number</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={roundForm.round_number}
                                    onChange={e => setRoundForm(p => ({ ...p, round_number: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Advancement Rule</Label>
                            <Select
                                value={roundForm.advancement_rule}
                                onValueChange={v => setRoundForm(p => ({ ...p, advancement_rule: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All players advance</SelectItem>
                                    <SelectItem value="top_n">Top N players advance</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {roundForm.advancement_rule === 'top_n' && (
                            <div className="space-y-1.5">
                                <Label>Number Who Advance</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    placeholder="e.g. 10"
                                    value={roundForm.advancement_count}
                                    onChange={e => setRoundForm(p => ({ ...p, advancement_count: e.target.value }))}
                                />
                            </div>
                        )}
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="carry_over"
                                checked={roundForm.carry_over_points}
                                onChange={e => setRoundForm(p => ({ ...p, carry_over_points: e.target.checked }))}
                                className="h-4 w-4 rounded border-input"
                            />
                            <Label htmlFor="carry_over" className="cursor-pointer">
                                Carry over points from previous rounds
                            </Label>
                        </div>
                        {!roundForm.carry_over_points && (
                            <p className="text-xs text-amber-500 bg-amber-500/10 px-3 py-2 rounded-md">
                                Points reset — leaderboard will only count scores from this round onwards.
                            </p>
                        )}
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="is_final_create"
                                checked={roundForm.is_final || false}
                                onChange={e => setRoundForm(p => ({ ...p, is_final: e.target.checked }))}
                                className="h-4 w-4 rounded border-input"
                            />
                            <Label htmlFor="is_final_create" className="cursor-pointer flex items-center gap-1.5">
                                🏆 Mark as the Final round
                            </Label>
                        </div>
                        {roundForm.is_final && (
                            <p className="text-xs text-amber-500 bg-amber-500/10 px-3 py-2 rounded-md">
                                When complete, the tournament champion podium will be shown to all participants.
                            </p>
                        )}
                        {bonusCatalog.length > 0 && (
                            <div className="space-y-2">
                                <Label>Bonus Questions</Label>
                                <p className="text-xs text-muted-foreground">Players answer these alongside their score prediction.</p>
                                <div className="border rounded-md divide-y">
                                    {bonusCatalog.map(q => (
                                        <label key={q.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/40">
                                            <input
                                                type="checkbox"
                                                checked={(roundForm.bonus_questions || []).includes(q.id)}
                                                onChange={e => setRoundForm(p => ({
                                                    ...p,
                                                    bonus_questions: e.target.checked
                                                        ? [...(p.bonus_questions || []), q.id]
                                                        : (p.bonus_questions || []).filter(id => id !== q.id)
                                                }))}
                                                className="h-4 w-4 rounded border-input flex-shrink-0"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm">{q.label}</p>
                                                <p className="text-xs text-muted-foreground">{q.points} pt{q.points !== 1 ? 's' : ''}{q.tiebreaker ? ' · tiebreaker' : ''}</p>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="flex-shrink-0 pt-2">
                        <Button variant="outline" onClick={() => setShowCreateRound(null)}>Cancel</Button>
                        <Button onClick={handleCreateRound} disabled={savingRound}>
                            {savingRound ? 'Creating…' : 'Create Round'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit round dialog */}
            <Dialog open={!!editingRound} onOpenChange={v => !v && setEditingRound(null)}>
                <DialogContent className="flex flex-col max-h-[90vh]">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>Edit Round</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2 overflow-y-auto flex-1 pr-1">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Round Name *</Label>
                                <Input
                                    value={roundForm.name}
                                    onChange={e => setRoundForm(p => ({ ...p, name: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Round Number</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={roundForm.round_number}
                                    onChange={e => setRoundForm(p => ({ ...p, round_number: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Status</Label>
                            <Select
                                value={roundForm.status}
                                onValueChange={v => setRoundForm(p => ({ ...p, status: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="upcoming">Upcoming (predictions locked)</SelectItem>
                                    <SelectItem value="active">Active (predictions open)</SelectItem>
                                    <SelectItem value="complete">Complete</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Advancement Rule</Label>
                            <Select
                                value={roundForm.advancement_rule}
                                onValueChange={v => setRoundForm(p => ({ ...p, advancement_rule: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All players advance</SelectItem>
                                    <SelectItem value="top_n">Top N players advance</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {roundForm.advancement_rule === 'top_n' && (
                            <div className="space-y-1.5">
                                <Label>Number Who Advance</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    placeholder="e.g. 10"
                                    value={roundForm.advancement_count}
                                    onChange={e => setRoundForm(p => ({ ...p, advancement_count: e.target.value }))}
                                />
                            </div>
                        )}
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="edit_carry_over"
                                checked={roundForm.carry_over_points}
                                onChange={e => setRoundForm(p => ({ ...p, carry_over_points: e.target.checked }))}
                                className="h-4 w-4 rounded border-input"
                            />
                            <Label htmlFor="edit_carry_over" className="cursor-pointer">
                                Carry over points from previous rounds
                            </Label>
                        </div>
                        {!roundForm.carry_over_points && (
                            <p className="text-xs text-amber-500 bg-amber-500/10 px-3 py-2 rounded-md">
                                Points reset — leaderboard will only count scores from this round onwards.
                            </p>
                        )}
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="is_final_edit"
                                checked={roundForm.is_final || false}
                                onChange={e => setRoundForm(p => ({ ...p, is_final: e.target.checked }))}
                                className="h-4 w-4 rounded border-input"
                            />
                            <Label htmlFor="is_final_edit" className="cursor-pointer flex items-center gap-1.5">
                                🏆 Mark as the Final round
                            </Label>
                        </div>
                        {roundForm.is_final && (
                            <p className="text-xs text-amber-500 bg-amber-500/10 px-3 py-2 rounded-md">
                                When complete, the tournament champion podium will be shown to all participants.
                            </p>
                        )}
                        {bonusCatalog.length > 0 && (
                            <div className="space-y-2">
                                <Label>Bonus Questions</Label>
                                <p className="text-xs text-muted-foreground">Players answer these alongside their score prediction. Points are awarded automatically at settlement.</p>
                                <div className="border rounded-md divide-y">
                                    {bonusCatalog.map(q => (
                                        <label key={q.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/40">
                                            <input
                                                type="checkbox"
                                                checked={(roundForm.bonus_questions || []).includes(q.id)}
                                                onChange={e => setRoundForm(p => ({
                                                    ...p,
                                                    bonus_questions: e.target.checked
                                                        ? [...(p.bonus_questions || []), q.id]
                                                        : (p.bonus_questions || []).filter(id => id !== q.id)
                                                }))}
                                                className="h-4 w-4 rounded border-input flex-shrink-0"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm">{q.label}</p>
                                                <p className="text-xs text-muted-foreground">{q.points} pt{q.points !== 1 ? 's' : ''}{q.tiebreaker ? ' · tiebreaker' : ''}</p>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="flex-shrink-0 pt-2">
                        <Button variant="outline" onClick={() => setEditingRound(null)}>Cancel</Button>
                        <Button onClick={handleSaveEditRound} disabled={savingRound}>
                            {savingRound ? 'Saving…' : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Match picker dialog */}
            <Dialog open={!!matchPicker} onOpenChange={v => !v && setMatchPicker(null)}>
                <DialogContent className="max-w-lg flex flex-col max-h-[90vh]">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>
                            Pick Matches — {matchPicker?.roundName}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2 overflow-y-auto flex-1">
                        {matchPicker && (() => {
                            const pool = matchPool[matchPicker.compId] || [];
                            if (pool.length === 0) {
                                return <p className="text-sm text-muted-foreground text-center py-4">No matches synced yet.</p>;
                            }
                            const grouped = groupMatchesByDate(pool);
                            return grouped.map(([date, dayMatches]) => (
                                <div key={date}>
                                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                                        {date}
                                    </p>
                                    <div className="space-y-1">
                                        {dayMatches.map(m => {
                                            const checked = pickerSelected.has(m.pa_match_id);
                                            const assignedElsewhere = m.round_id && m.round_id !== matchPicker.roundId;
                                            return (
                                                <label
                                                    key={m.pa_match_id}
                                                    className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                                                        checked ? 'bg-primary/10 border border-primary/30' : 'bg-muted/40 hover:bg-muted/60'
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={e => {
                                                            setPickerSelected(prev => {
                                                                const next = new Set(prev);
                                                                if (e.target.checked) next.add(m.pa_match_id);
                                                                else next.delete(m.pa_match_id);
                                                                return next;
                                                            });
                                                        }}
                                                        className="h-4 w-4 flex-shrink-0"
                                                    />
                                                    <span className="flex-1 text-sm font-medium">
                                                        {m.home_team_name} v {m.away_team_name}
                                                    </span>
                                                    {assignedElsewhere && (
                                                        <span className="text-xs text-amber-500">other round</span>
                                                    )}
                                                    <span className="text-xs text-muted-foreground">
                                                        {m.kickoff_time ? m.kickoff_time.slice(11, 16) : ''}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ));
                        })()}
                    </div>
                    <div className="flex-shrink-0 pt-1">
                        <p className="text-xs text-muted-foreground px-1 mb-2">
                            {pickerSelected.size} match{pickerSelected.size !== 1 ? 'es' : ''} selected
                        </p>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setMatchPicker(null)}>Cancel</Button>
                            <Button onClick={handleSavePicker} disabled={savingPicker}>
                                {savingPicker ? 'Saving…' : 'Save Assignment'}
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ── Delete round confirmation ── */}
            <Dialog open={!!deleteRoundConfirm} onOpenChange={open => { if (!open) { setDeleteRoundConfirm(null); setDeleteRoundInput(''); }}}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="text-red-500">Delete Round</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-400 space-y-1">
                            <p className="font-semibold">This cannot be undone.</p>
                            <p>Deleting <span className="font-bold text-red-300">"{deleteRoundConfirm?.roundName}"</span> will unassign all its matches and remove all predictions for this round.</p>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-sm text-muted-foreground">
                                Type <span className="font-bold text-foreground">DELETE</span> to confirm
                            </Label>
                            <Input
                                value={deleteRoundInput}
                                onChange={e => setDeleteRoundInput(e.target.value)}
                                placeholder="DELETE"
                                className="font-mono"
                                autoFocus
                                onKeyDown={e => { if (e.key === 'Enter' && deleteRoundInput === 'DELETE') confirmDeleteRound(); }}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setDeleteRoundConfirm(null); setDeleteRoundInput(''); }}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={deleteRoundInput !== 'DELETE'}
                            onClick={confirmDeleteRound}
                        >
                            Delete Round
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
